import { useEffect, useState } from "react";
import { Send, AudioLines, Mic, Loader2, Radio } from "lucide-react";
import { useDictation } from "@/hooks/useDictation";
import { langLocale, langName } from "@/lib/langs";

const RecordingBar = () => {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSec((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex h-12 min-w-0 flex-1 items-center gap-3 rounded-full border border-[color:var(--au-red)] bg-red-500/10 px-5" data-testid="room-recording-bar">
      <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-[color:var(--au-red)]" />
      <span className="font-mono text-sm tabular-nums text-[color:var(--au-red)]">{Math.floor(sec / 60)}:{String(sec % 60).padStart(2, "0")}</span>
      <span className="truncate text-xs text-muted-foreground">Recording… release or tap the mic to send</span>
    </div>
  );
};

const MicButton = ({ rec, processing, handlers }) => (
  <div className="relative shrink-0">
    {rec.recording && <span className="ripple absolute inset-0 rounded-full bg-[color:var(--au-red)]" />}
    {!rec.recording && !processing && <span className="absolute -inset-1 rounded-full border border-cyan-400/40 arc-pulse" />}
    <button type="button" {...handlers} title="Hold to talk · tap to start/stop · Space"
      className={`relative flex h-12 w-12 touch-none select-none items-center justify-center rounded-full transition-[transform,background-color,box-shadow] duration-200 hover:scale-105 ${rec.recording ? "scale-110 bg-[color:var(--au-red)] text-white shadow-[0_0_24px_rgba(255,59,48,0.6)]" : "bg-[#3DDBE8] text-[#0A0A0B] shadow-[0_0_20px_rgba(61,219,232,0.45)]"}`}
      data-testid="room-mic-button" aria-label="Record voice message">
      {processing ? <Loader2 size={20} className="animate-spin" /> : <Mic size={21} strokeWidth={2.4} />}
    </button>
  </div>
);

export const Composer = ({ myLang, onSend, sending, rec, pttHandlers, processing, handsFree, hf, onToggleHandsFree }) => {
  const [text, setText] = useState("");
  const [dictated, setDictated] = useState(false);
  const dict = useDictation(langLocale(myLang), (t) => {
    setText(t);
    setDictated(true);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    if (dict.active) dict.toggle();
    const ok = await onSend(text.trim(), dictated ? "dictation" : "text");
    if (ok) {
      setText("");
      setDictated(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-border p-3 sm:p-4" data-testid="room-composer">
      <div className="flex items-center gap-2 sm:gap-3">
        <button type="button" onClick={onToggleHandsFree} title="Hands-free mode"
          className={`flex h-11 w-11 shrink-0 items-center justify-center border transition-colors duration-200 lg:hidden ${handsFree ? "arc-pulse border-[color:var(--au-cyan)] text-[color:var(--au-cyan)]" : "border-border text-muted-foreground"}`}
          data-testid="room-handsfree-mobile-toggle" aria-label="Hands-free mode">
          <Radio size={18} className={hf?.recording ? "animate-pulse" : ""} />
        </button>
        {rec.recording ? (
          <RecordingBar />
        ) : (
          <div className="relative min-w-0 flex-1">
            <input className="field !h-12 !rounded-full !pl-5 !pr-12" value={text} onChange={(e) => { setText(e.target.value); if (!e.target.value) setDictated(false); }}
              placeholder={dict.active ? "Listening… speak now" : handsFree ? "Hands-free on — just speak, or type…" : `Type in ${langName(myLang)}…`} data-testid="room-dictation-input" />
            <button type="button" onClick={() => dict.toggle(text)} title="Dictate into the text box"
              className={`absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full transition-colors duration-200 ${dict.active ? "arc-pulse text-[color:var(--au-green)]" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="room-dictation-button" aria-label="Dictate">
              <AudioLines size={17} />
            </button>
          </div>
        )}
        {!handsFree && (
          <MicButton rec={rec} processing={processing} handlers={pttHandlers} />
        )}
        <button className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-[transform,opacity] duration-200 hover:scale-105 disabled:opacity-30 disabled:hover:scale-100"
          disabled={sending || !text.trim()} data-testid="room-send-message-button" aria-label="Send message">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
      {!handsFree && !rec.recording && (
        <p className="mt-2 text-center font-mono text-[10px] tracking-widest text-muted-foreground lg:hidden">HOLD <Mic size={10} className="inline" /> TO TALK · TAP TO START / STOP</p>
      )}
    </form>
  );
};
